import { getSourcesInfo, SourceInfo } from "@constants/sources";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Typography, IconButton, Drawer } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

export interface SourcesToastProps {
  open: boolean;
  onClose: () => void;
  sourceIds: string[];
}

const SourcesToast: React.FC<SourcesToastProps> = ({
  open,
  onClose,
  sourceIds
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [startY, setStartY] = useState(0);
  const [currentY, setCurrentY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const contentWrapperRef = React.useRef<HTMLDivElement>(null);
  const lastScrollTopRef = React.useRef(0);

  const sources = getSourcesInfo(sourceIds);

  // Reset expanded state when toast opens/closes
  useEffect(() => {
    if (open) {
      setIsExpanded(false);
      setCurrentY(0);
      setIsDragging(false);
    }
  }, [open]);

  // Handle touch start for drag (on header/grabber)
  const handleTouchStart = (e: React.TouchEvent) => {
    setStartY(e.touches[0].clientY);
    setIsDragging(true);
    setCurrentY(0);
  };

  // Handle touch move for drag
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    e.preventDefault(); // Prevent default scrolling
    const currentClientY = e.touches[0].clientY;
    const deltaY = currentClientY - startY;

    if (deltaY > 0) {
      // Dragging down - closing
      setCurrentY(deltaY);
    } else if (deltaY < -50 && !isExpanded) {
      // Dragging up more than 50px - expand
      setIsExpanded(true);
      setIsDragging(false);
      setCurrentY(0);
    }
  };

  // Handle touch end
  const handleTouchEnd = () => {
    if (currentY > 100) {
      // Dragged down more than 100px - close
      onClose();
    } else {
      // Snap back
      setCurrentY(0);
    }
    setIsDragging(false);
  };

  // Handle wheel/scroll to expand when at top
  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    // If at the top (scrollTop === 0) and scrolling up (deltaY < 0), expand
    if (target.scrollTop === 0 && e.deltaY < 0 && !isExpanded) {
      setIsExpanded(true);
    }
  };

  // Handle scroll to detect when at top and trying to scroll up
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const currentScrollTop = target.scrollTop;

    // If at the top and scroll direction is up (decreasing), expand
    if (currentScrollTop === 0 && lastScrollTopRef.current > 0 && !isExpanded) {
      setIsExpanded(true);
    }

    lastScrollTopRef.current = currentScrollTop;
  };

  // Don't render if not open or no valid sources found
  if (!open) {
    return null;
  }

  if (sources.length === 0) {
    return null;
  }

  const drawerHeight = isExpanded ? "100%" : "50%";
  const translateY = isDragging ? currentY : 0;

  return (
    <>
      {/* Overlay */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: "rgba(0, 0, 0, 0.2)",
          zIndex: 1299,
          display: open ? "block" : "none"
        }}
        onClick={onClose}
      />

      {/* Toast Drawer */}
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            height: drawerHeight,
            maxHeight: "100%",
            maxWidth: { xs: "100%", sm: 800 },
            width: "100%",
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            bgcolor: theme.palette.customColors.chipBg,
            transition: isDragging ? "none" : "height 0.3s ease-in-out",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            left: { xs: 0, sm: "50%" },
            transform: {
              xs: `translateY(${translateY}px)`,
              sm: `translate(-50%, ${translateY}px)`
            }
          }
        }}
        ModalProps={{
          keepMounted: false
        }}
        sx={{
          zIndex: 1300
        }}
      >
        {/* Header - Fixed, no scroll, height 68px */}
        <Box
          id="toast-header"
          sx={{
            height: "68px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            borderBottom: "none",
            flexShrink: 0
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Grabber */}
          <Box
            sx={{
              position: "absolute",
              top: 8,
              left: "50%",
              transform: "translateX(-50%)",
              width: 40,
              height: 4,
              bgcolor: theme.palette.customColors.textDisabled,
              borderRadius: 2,
              cursor: "grab"
            }}
          />

          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 600,
              color: theme.palette.text.primary,
              ml: "auto",
              mr: "auto"
            }}
          >
            {t("sources.toast.title", "Fonts consultades")}
          </Typography>

          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: theme.palette.text.primary,
              "&:hover": {
                bgcolor: theme.palette.customColors.hoverBackground
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content Wrapper - Scrollable, expandable */}
        <Box
          id="toast-content-wrapper"
          ref={contentWrapperRef}
          onScroll={handleScroll}
          onWheel={handleWheel}
          sx={{
            flex: 1,
            overflow: "auto",
            px: 2,
            py: 2
          }}
        >
          {sources.map((source, index) => {
            const SourceCard = ({ source }: { source: SourceInfo }) => {
              const [faviconError, setFaviconError] = useState(false);
              const domain = source.url.replace(/^https?:\/\//, "");

              return (
                <Box
                  component="a"
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                    p: "16px",
                    mb: 1.5,
                    bgcolor: theme.palette.background.paper,
                    borderRadius: "16px",
                    boxShadow: "none",
                    textDecoration: "none",
                    color: "inherit",
                    "&:hover": {
                      bgcolor: theme.palette.customColors.hoverBackground
                    }
                  }}
                >
                  {/* Title */}
                  <Typography
                    id="text-block"
                    sx={{
                      fontFamily: "Ringside Narrow",
                      fontSize: "18px",
                      fontWeight: 325,
                      fontStyle: "normal",
                      lineHeight: "24px",
                      letterSpacing: "0%",
                      color: "#000000"
                    }}
                  >
                    {source.name}
                  </Typography>

                  {/* URL Block */}
                  <Box
                    id="url-block"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1
                    }}
                  >
                    {!faviconError ? (
                      <Box
                        component="img"
                        src={`https://www.google.com/s2/favicons?domain=${domain}&sz=16`}
                        alt=""
                        onError={() => setFaviconError(true)}
                        sx={{
                          width: 16,
                          height: 16,
                          flexShrink: 0,
                          objectFit: "contain"
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: "50%",
                          bgcolor: theme.palette.primary.main,
                          flexShrink: 0
                        }}
                      />
                    )}
                    <Typography
                      sx={{
                        fontFamily: "Ringside Narrow",
                        fontSize: "16px",
                        fontWeight: 325,
                        fontStyle: "normal",
                        lineHeight: "20px",
                        letterSpacing: "0%",
                        color: "#333333",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                      }}
                    >
                      {domain}
                    </Typography>
                  </Box>
                </Box>
              );
            };

            return <SourceCard key={index} source={source} />;
          })}
        </Box>
      </Drawer>
    </>
  );
};

export default SourcesToast;
